import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { useMutation } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
	fetchRealtorIdProof,
	type ReviewableRealtorProfile,
} from "../services/approvals-api.service";

const NOT_PROVIDED = "Not provided";

function DetailField({
	label,
	children,
}: {
	label: string;
	children: ReactNode;
}) {
	return (
		<div className="grid gap-1">
			<span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
				{label}
			</span>
			{children}
		</div>
	);
}

function textOrNotProvided(value: string | null | undefined): string {
	return value?.trim() || NOT_PROVIDED;
}

function listOrNotProvided(values: string[] | null | undefined): string {
	return values?.length ? values.join(", ") : NOT_PROVIDED;
}

function yearsOrNotProvided(years: number | null | undefined): string {
	if (years === null || years === undefined) return NOT_PROVIDED;
	return years === 1 ? "1 year" : `${years} years`;
}

function VerificationBadge({
	label,
	verified,
}: {
	label: string;
	verified: boolean;
}) {
	return (
		<Badge variant={verified ? "success" : "warning"}>
			{label} {verified ? "verified" : "not verified"}
		</Badge>
	);
}

interface LoadedIdProof {
	url: string;
	contentType: string;
}

/**
 * The submitted ID proof, fetched on demand. The file is private, so it is
 * read through the admin route as a Blob and shown from an object URL: inline
 * when it is an image, and as a link to open it in a new tab either way.
 */
function IdProofViewer({ profile }: { profile: ReviewableRealtorProfile }) {
	const [idProofBlob, setIdProofBlob] = useState<Blob | null>(null);
	const [idProof, setIdProof] = useState<LoadedIdProof | null>(null);

	const view = useMutation({
		mutationFn: () => fetchRealtorIdProof(profile.id),
		onSuccess: (blob) => setIdProofBlob(blob),
	});

	useEffect(() => {
		if (!idProofBlob) return;
		const url = URL.createObjectURL(idProofBlob);
		setIdProof({ url, contentType: idProofBlob.type });
		return () => URL.revokeObjectURL(url);
	}, [idProofBlob]);

	if (!idProof) {
		return (
			<div className="grid justify-items-start gap-2">
				<Button
					variant="outline"
					size="sm"
					onClick={() => view.mutate()}
					isLoading={view.isPending}
				>
					View ID proof
				</Button>
				{view.isError ? (
					<p role="alert" className="text-sm text-rose-700">
						The ID proof could not be loaded.
					</p>
				) : null}
			</div>
		);
	}

	return (
		<div className="grid gap-2">
			{idProof.contentType.startsWith("image/") ? (
				<img
					src={idProof.url}
					alt={`ID proof submitted by ${profile.name}`}
					className="max-h-96 w-full rounded-lg border border-slate-200 object-contain"
				/>
			) : null}
			<a
				href={idProof.url}
				target="_blank"
				rel="noopener noreferrer"
				className="text-sm font-medium text-primary underline-offset-4 hover:underline"
			>
				Open ID proof in a new tab
			</a>
		</div>
	);
}

/**
 * A realtor's RERA registration, practice and ID proof, shown in the
 * approvals sheet and in a realtor's user detail. Viewing the ID proof file
 * needs `admin:approvals:read`, so a screen an operator can reach without it
 * passes `canViewIdProof={false}` and the file is only reported as uploaded.
 */
export function RealtorReviewDetails({
	profile,
	canViewIdProof = true,
}: {
	profile: ReviewableRealtorProfile;
	canViewIdProof?: boolean;
}) {
	return (
		<Card className="border-slate-200 shadow-none">
			<CardHeader>
				<CardTitle>Realtor details</CardTitle>
				<CardDescription>
					The RERA registration and ID proof the realtor submitted for
					verification.
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4 text-sm">
				<DetailField label="RERA number">
					<span className="font-medium">
						{textOrNotProvided(profile.reraNumber)}
					</span>
				</DetailField>
				<DetailField label="Agency">
					<span>{textOrNotProvided(profile.agencyName)}</span>
				</DetailField>
				<DetailField label="Service cities">
					<span>{listOrNotProvided(profile.serviceCities)}</span>
				</DetailField>
				<DetailField label="Service localities">
					<span>{listOrNotProvided(profile.serviceLocalities)}</span>
				</DetailField>
				<DetailField label="Experience">
					<span>{yearsOrNotProvided(profile.yearsExperience)}</span>
				</DetailField>
				<DetailField label="Verification">
					<div className="flex flex-wrap gap-2">
						<VerificationBadge label="RERA" verified={profile.reraVerified} />
						<VerificationBadge label="ID" verified={profile.idVerified} />
					</div>
				</DetailField>
				<DetailField label="ID proof">
					<span>{profile.idProofUploaded ? "Uploaded" : "Not uploaded"}</span>
					{profile.idProofUploaded && canViewIdProof ? (
						<IdProofViewer profile={profile} />
					) : null}
				</DetailField>
			</CardContent>
		</Card>
	);
}
